import { CheckCircle2Icon } from 'lucide-react'
import { forwardRef } from 'react'

import { Divider } from '@/components/divider/divider'
import { Input } from '@/components/input/input'
import { Label } from '@/components/label/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/select/select'

import { type ResApplicantCandidateItem } from '@/lib/queries/business/v1/ocr/types'
import { cn } from '@/lib/utils'

import { SELECT_ITEM_USE_SCANNED_NAME } from './constants'
import { useExistingApplicantNameInput } from './use-existing-applicant-name-input'

export interface ExistingApplicantNameInputProps {
  applicantCandidateList?: ResApplicantCandidateItem[]
  ocrScannedValue?: string
  matchingValue?: string
  disabled?: boolean
  onChangeScannedValue: (value: string) => void
  onChangeMatchingValue: (value: string) => void
}

export const ExistingApplicantNameInput = forwardRef<HTMLDivElement, ExistingApplicantNameInputProps>((props, ref) => {
  const {
    ocrScannedValue,
    matchingValue,
    usesScannedName,
    isDisabledOCRScannedNameInput,
    isDisabledSelectApplicantInput,
    handleChangeScannedInput,
    handleChangeSelectedValue,
  } = useExistingApplicantNameInput(props)

  return (
    <div ref={ref} className={cn('')}>
      <div className={cn('flex flex-col gap-y-2')}>
        <Label className={cn('text-xs text-slate-700')}>Applicant name from scan (max 50 characters)</Label>
        <Input
          disabled={isDisabledOCRScannedNameInput}
          value={ocrScannedValue ?? ''}
          onChange={handleChangeScannedInput}
        />
      </div>
      {usesScannedName && <UseThisNameMarker text="Scanned name will be used" className={cn('mt-1')} />}
      <InputDivider />
      <div className={cn('flex flex-col gap-y-2')}>
        <Label className={cn('text-xs text-slate-700')}>Matching applicant name in Lawfully Pro</Label>
        <Select
          disabled={isDisabledSelectApplicantInput}
          value={matchingValue}
          onValueChange={handleChangeSelectedValue}
        >
          <SelectTrigger>
            <SelectValue placeholder="Check for matching name" />
          </SelectTrigger>
          <SelectContent>
            {props.applicantCandidateList?.map(({ applicantId, applicantName, applicantEmail }) => (
              <SelectItem key={applicantId} value={`${applicantId}`}>
                {`${applicantName} (${applicantEmail})`}
              </SelectItem>
            ))}
            <SelectSeparator className={cn('my-1')} />
            <SelectItem key={SELECT_ITEM_USE_SCANNED_NAME.value} value={SELECT_ITEM_USE_SCANNED_NAME.value}>
              {SELECT_ITEM_USE_SCANNED_NAME.label}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {!usesScannedName && (
        <UseThisNameMarker
          text={`Matching name will be used. To use scanned name, select "${SELECT_ITEM_USE_SCANNED_NAME.label}"`}
          className={cn('mt-1')}
        />
      )}
      <Divider className={cn('mt-5')} />
    </div>
  )
})
ExistingApplicantNameInput.displayName = 'ExistingApplicantNameInput'

const InputDivider = () => {
  return (
    <div className={cn('relative', 'my-3.5')}>
      <Divider className={cn('my-0')} />
      <span
        className={cn(
          'absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
          'inline-block',
          'text-xs font-normal text-slate-400',
          'px-2',
          'bg-white'
        )}
      >
        OR
      </span>
    </div>
  )
}

const UseThisNameMarker = ({ text, className }: { text: string; className?: string }) => {
  return (
    <div className={cn('flex items-start gap-x-1', className)}>
      <CheckCircle2Icon size={20} className={cn('fill-blue-600 stroke-white', 'min-w-5')} />
      <span className={cn('text-xs font-normal leading-5 text-slate-700')}>{text}</span>
    </div>
  )
}
